import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Combobox } from '../../components/ui/Combobox';
import { PageHeader } from '../../components/layout/PageHeader';
import { useCurrentUser } from '../auth';
import { computeDisplayName, type Contact } from '../../types/contact';
import { listContacts } from './api';
import { listAccounts } from '../accounts/api';

function matchesSearch(c: Contact, q: string): boolean {
  if (q === '') return true;
  const needle = q.toLowerCase();
  return (
    computeDisplayName(c).toLowerCase().includes(needle) ||
    (c.email?.toLowerCase().includes(needle) ?? false) ||
    (c.phone?.toLowerCase().includes(needle) ?? false)
  );
}

export function ContactList() {
  const user = useCurrentUser();
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState<string>('');

  const contacts = useQuery({
    queryKey: ['contacts', accountFilter || null],
    queryFn: () => listContacts(accountFilter || undefined),
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const accountById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts.data ?? []) m.set(a.id, a.name);
    return m;
  }, [accounts.data]);

  const rows = useMemo(() => {
    return (contacts.data ?? []).filter((c) => matchesSearch(c, search));
  }, [contacts.data, search]);

  if (contacts.isLoading) return <CenteredSpinner />;
  if (contacts.error) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load contacts: {(contacts.error as Error).message}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Contacts"
        description="People linked to one or more accounts."
        actions={
          user.role === 'admin' ? (
            <>
              <Button variant="outline" asChild>
                <Link to="/contacts/bulk-upload">Bulk upload</Link>
              </Button>
              <Button asChild>
                <Link to="/contacts/new">New contact</Link>
              </Button>
            </>
          ) : undefined
        }
      />

      <div className="flex items-center gap-3 mb-4">
        <Input
          className="w-64"
          placeholder="Search name / email / phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Combobox
          aria-label="Filter by account"
          searchPlaceholder="Search accounts…"
          value={accountFilter}
          onValueChange={setAccountFilter}
          options={[
            { value: '', label: 'All accounts' },
            ...(accounts.data ?? []).map((a) => ({ value: a.id, label: a.name })),
          ]}
        />
      </div>

      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          No contacts match.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">Linked accounts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => {
                const primaryCount = c.accountLinks.filter((l) => l.isPrimary).length;
                // Stage 3 §2.3. Show channel badges so AMs can scan at a
                // glance which contacts are reachable via which channel.
                // Both flags must be on AND the corresponding field
                // (email / phone) must be non-empty -- the form-level
                // gate keeps these in sync but we re-check here to stay
                // defensive against legacy data.
                const hasEmailChannel = c.channelEmail && c.email !== null;
                const hasSmsChannel = c.channelSMS && c.phone !== null;
                return (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={`/contacts/${c.id}`} className="font-medium text-slate-900 hover:underline">
                        {computeDisplayName(c) || '(no name)'}
                      </Link>
                      {primaryCount > 0 && (
                        <span className="ml-2 inline-block text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                          primary × {primaryCount}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="flex items-center gap-2">
                        <span>{c.email ?? '—'}</span>
                        {hasEmailChannel && (
                          <span
                            className="inline-flex items-center text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700"
                            title="Opted in to email previews"
                            aria-label="Email channel on"
                          >
                            📧
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="flex items-center gap-2">
                        <span>{c.phone ?? '—'}</span>
                        {hasSmsChannel && (
                          <span
                            className="inline-flex items-center text-xs px-1.5 py-0.5 rounded bg-sky-100 text-sky-700"
                            title="Opted in to SMS previews"
                            aria-label="SMS channel on"
                          >
                            📱
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {c.accountLinks.length === 0
                        ? '—'
                        : c.accountLinks
                            .map((l) => accountById.get(l.accountId) ?? l.accountId)
                            .join(', ')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
